import { addHours } from "date-fns";
import type { TFunction } from "i18next";
import _ from "lodash";

import apolloClient from "@/apollo";
import { addDangerFlash, addSuccessFlash } from "@/components/flash/flashReducer";
import { MutateEventDocument } from "@/gql/graphql";
import { EventTypesDocument, MutateContactDocument } from "@/gql/graphql";
import { type ContactStatusFragment, MutateTaskDocument } from "@/gql/graphql";
import handleError, { MutationError } from "@/handleError";
import store from "@/store";
import { naiveDateTime } from "@/utils/dates";

import type { ValuesType } from "./formUtils";

function determineEventType(type: string, values: ValuesType) {
  if (type === "Wiedervorlage") {
    return "FOLLOWUP";
  }
  return values.attrs.appointmentChannel === "Teams-Termin (Online)" ? "PHONE_EVENT" : "EVENT";
}

function generateDescription(contact: ContactFull, values: ValuesType) {
  const address = contact?.primaryAddress;

  let desc = `${address?.address || ""} ${address?.zip || ""} ${address?.city || ""}\n`;

  if (values.attrs.landlineProvider) {
    desc += `Festnetzanbieter: ${values.attrs.landlineProvider}\n`;
  }

  if (values.attrs.mobileProvider) {
    desc += `Mobilanbieter: ${values.attrs.mobileProvider}\n`;
  }

  if (values.attrs.internetProvider) {
    desc += `Internetanbieter: ${values.attrs.internetProvider}\n`;
  }

  if (values.attrs.simCards) {
    desc += `SIM-Karten Anzahl: ${values.attrs.simCards}\n`;
  }

  if (values.description) {
    desc += `Gesprächsnotiz: ${values.description}\n`;
  }

  return desc;
}

async function createEvent(
  session: SessionInterface,
  contact: any,
  type: "Wiedervorlage" | "Termin",
  values: ValuesType,
  t: TFunction,
) {
  try {
    const { data: eventTypeData } = await apolloClient.query({
      query: EventTypesDocument,
      variables: {
        customerId: session.currentCustomer.id,
        projectId: session.currentProject.id,
      },
    });

    const eType = determineEventType(type, values);
    const eventType = eventTypeData?.eventTypes.find((eventType) => eventType.type === eType);

    if (!eventType) {
      store.dispatch(addDangerFlash(`Oops! Es konnte kein ${eType}-Event-Typ gefunden werden!`));
    }

    const startTime =
      type === "Wiedervorlage" ? naiveDateTime(values.attrs.followUp) : naiveDateTime(values.attrs.arrangedAppointment);

    const endTime =
      type === "Wiedervorlage"
        ? naiveDateTime(values.attrs.followUp)
        : naiveDateTime(addHours(values.attrs.arrangedAppointment || new Date(), 1));

    const details = type === "Termin" ? generateDescription(contact, values) : "";

    const { data } = await apolloClient.mutate({
      mutation: MutateEventDocument,
      variables: {
        customerId: session.currentCustomer.id,
        projectId: session.currentProject.id,
        event: {
          subject: type === "Wiedervorlage" ? type : values.attrs.appointmentChannel,
          details,
          allDay: false,
          startTime,
          endTime,
          contactId: values.contactId,
          ownerId: type === "Wiedervorlage" ? session.currentUser.id : values.attrs.salesEmployeeId,
          typeId: eventType?.id,
          attrs: {
            teamsUrl: values.attrs.teamsUrl,
            contactPersonFirstname: values.attrs.contactPerson.firstname,
            contactPersonLastname: values.attrs.contactPerson.lastname,
            meetingMail: values.attrs.contactPerson.mail,
          },
        },
      },
    });

    if (!data?.mutateEvent) {
      throw new MutationError();
    }
  } catch (e) {
    store.dispatch(addDangerFlash(t("translation:global.general_error")));
    handleError(e);
  }
}

async function mutateContactChild(session: SessionInterface, values: ValuesType, t: TFunction) {
  try {
    const { data } = await apolloClient.mutate({
      mutation: MutateContactDocument,
      variables: {
        customerId: session.currentCustomer.id,
        projectId: session.currentProject.id,
        id: !values.attrs.contactPersonId ? null : values.attrs.contactPersonId,
        contact: {
          ctype: "SUB",
          parentId: values.contactId,
          isCompany: false,
          salutation: values.attrs.contactPerson.salutation,
          firstname: values.attrs.contactPerson.firstname,
          lastname: values.attrs.contactPerson.lastname,
          position: values.attrs.contactPerson.position,
          infos: [
            { itype: "WORK_PHONE" as const, value: values.attrs.contactPerson.phone },
            { itype: "EMAIL" as const, value: values.attrs.contactPerson.mail },
          ].filter((v) => !!v.value),
        },
      },
    });

    if (!data?.mutateContact) {
      throw new MutationError();
    }
  } catch (e) {
    store.dispatch(addDangerFlash(t("translation:global.general_error")));
    handleError(e);
  }
}

export const saveTask = async (
  session: SessionInterface,
  existingTask: Nilable<CCTaskInput>,
  states: ContactStatusFragment[],
  values: ValuesType,
  t: TFunction,
) => {
  if (!values) {
    store.dispatch(addDangerFlash(t("translation:global.general_error")));
    handleError(new Error("saveTask called without values"));
    return;
  }

  const appointmentBooked =
    values.attrs.postponementResult === "direkte Terminierung" || values.attrs.result === "Termin";

  if (appointmentBooked) {
    createEvent(session, values.contact, "Termin", values, t);
  }

  if (!!values.attrs.contactPerson.firstname || !!values.attrs.contactPerson.lastname) {
    mutateContactChild(session, values, t);
  }

  if (values.attrs.followUp) {
    createEvent(session, values.contact, "Wiedervorlage", values, t);
  }

  const { statusId, caddfields, contact } = values;

  try {
    const { data } = await apolloClient.mutate({
      mutation: MutateTaskDocument,
      variables: {
        values: _.omit(values, ["infos", "contact", "statusId", "caddfields"]),
        customerId: session.currentCustomer.id,
        projectId: session.currentProject.id,
        id: existingTask?.id,
      },
    });

    if (!data?.mutateTask) {
      throw new MutationError();
    }

    if ((statusId || caddfields) && contact) {
      const status = states.find((s) => s.id === statusId);

      const cnt: Record<string, any> = {};
      cnt.attrs = {
        ...contact.attrs,
        addfields: { ...contact.attrs.addfields, ...caddfields },
      };

      const { data } = await apolloClient.mutate({
        mutation: MutateContactDocument,
        variables: {
          customerId: session.currentCustomer.id,
          projectId: session.currentProject.id,
          id: contact.id,
          contact: { ...(cnt || {}), statusId: status?.id },
        },
      });

      if (!data?.mutateContact) {
        throw new MutationError();
      }
    }

    const transString = existingTask?.id ? "customerContacts:edit_form.updated" : "customerContacts:new_form.created";

    store.dispatch(addSuccessFlash(t(transString)));

    return data.mutateTask;
  } catch (e) {
    store.dispatch(addDangerFlash(t("translation:global.general_error")));
    handleError(e);
  }
};
